import { useState } from "react";
import { Card, CardContent, Button } from "@learn-me/ui";
import { useCourseWizard } from "@/hooks";
import {
  ChevronDown,
  ChevronUp,
  Edit2,
  Eye,
  FileText,
  HelpCircle,
  CheckCircle,
  Sparkles,
} from "lucide-react";

export function WizardStep5Review() {
  const {
    courseTitle,
    courseDescription,
    shortDescription,
    generatedChapters,
  } = useCourseWizard();

  const [expandedChapters, setExpandedChapters] = useState<Set<number>>(
    new Set(),
  );

  const toggleChapter = (index: number) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedChapters(newExpanded);
  };

  const expandAll = () => {
    setExpandedChapters(new Set(generatedChapters.map((_, index) => index)));
  };

  const collapseAll = () => {
    setExpandedChapters(new Set());
  };

  const totalDuration = generatedChapters.reduce(
    (acc, ch) => acc + ch.estimatedDuration,
    0,
  );
  const chaptersWithQuiz = generatedChapters.filter(
    (ch) => ch.includeQuiz,
  ).length;

  return (
    <div className="space-y-6">
      {/* Summary card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {courseTitle}
              </h2>
              <p className="text-sm text-gray-500 mt-1">{shortDescription}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={expandAll}>
                <Eye className="h-4 w-4 mr-1" />
                Expandir todo
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                Colapsar
              </Button>
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 line-clamp-3">
              {courseDescription}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="text-center p-3 bg-primary-50 rounded-lg">
              <div className="text-2xl font-bold text-primary-700">
                {generatedChapters.length}
              </div>
              <div className="text-xs text-primary-600">Capitulos</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">
                {totalDuration}
              </div>
              <div className="text-xs text-green-600">Minutos</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-700">
                {chaptersWithQuiz}
              </div>
              <div className="text-xs text-purple-600">Quizzes</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chapters review */}
      <div className="space-y-3">
        {generatedChapters.map((chapter, index) => {
          const isExpanded = expandedChapters.has(index);

          return (
            <Card key={index}>
              <CardContent className="p-0">
                {/* Chapter header */}
                <button
                  type="button"
                  onClick={() => toggleChapter(index)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </div>
                    <div className="text-left">
                      <h4 className="font-medium text-gray-900">
                        {chapter.title}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {chapter.estimatedDuration} minutos
                        {chapter.includeQuiz && " • Con quiz"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t px-4 pb-4">
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Descripcion
                      </h5>
                      <p className="text-sm text-gray-600">
                        {chapter.description}
                      </p>
                    </div>

                    {/* Sections preview */}
                    {chapter.sections && chapter.sections.length > 0 && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Secciones ({chapter.sections.length})
                        </h5>
                        <div className="space-y-2">
                          {chapter.sections.slice(0, 3).map((section, sIdx) => (
                            <div
                              key={sIdx}
                              className="p-2 bg-gray-50 rounded text-sm"
                            >
                              <span className="font-medium text-gray-700">
                                {sIdx + 1}. {section.title}
                              </span>
                            </div>
                          ))}
                          {chapter.sections.length > 3 && (
                            <p className="text-xs text-gray-500 pl-2">
                              + {chapter.sections.length - 3} secciones mas...
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Quiz preview */}
                    {chapter.includeQuiz && chapter.quiz && (
                      <div className="mt-4">
                        <h5 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <HelpCircle className="h-4 w-4" />
                          Quiz: {chapter.quiz.title}
                        </h5>
                        <p className="text-sm text-gray-500">
                          {chapter.quiz.questions?.length || 5} preguntas
                        </p>
                      </div>
                    )}

                    {/* Note about editing */}
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-700">
                        <Sparkles className="h-3 w-3 inline mr-1" />
                        Podras editar el contenido detallado despues de crear el
                        curso en el editor de capitulos.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Ready to publish notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800">
              Listo para publicar
            </p>
            <p className="text-sm text-green-700 mt-1">
              El contenido ha sido generado y esta listo. En el siguiente paso
              podras configurar el precio y publicar el curso. Tambien podras
              hacer ajustes detallados despues de la publicacion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
